/**
 * 전체화면 창(window) 스택 store 다. 프레임워크·도메인에 의존하지 않는 순수 상태 머신이다.
 *
 * Owns:
 * - 열린 창 엔트리 목록(최대 MAX_WINDOWS)과 단일 활성 불변식. 불변식은 "지금 하나만 전체화면으로
 *   보인다(activeId)"이다.
 * - open/setActive/minimize/close 의 stacking 정책:
 *   - 동시에 1개만 표시한다.
 *   - 새로 열면 직전 활성 창을 자동으로 최소화한다.
 *   - cap 을 넘으면 차단한다(null 반환, bypassCap 예외).
 *   - 같은 (kind,key) 중복을 막는다.
 *   - singleton kind 는 원자적으로 교체한다.
 * - 엔트리 closable 불변식: closable=false 인 엔트리는 close 로 닫을 수 없다. 진행 중 업로드 창처럼
 *   완료/폐기 전 닫기를 막을 때 쓴다. 닫으려면 먼저 update 로 closable=true 로 바꿔야 한다.
 * - 엔트리별 onClose lifecycle 훅(닫힐 때 1회 호출 후 폐기).
 *
 * Does not own:
 * - cap 초과 안내(예: toast)·kind 집합·싱글턴 인스턴스. 소비처(앱)가 소유한다.
 * - 각 kind 의 콘텐츠 렌더링과 params 해석. 호스트가 renderWindow 로 주입한다.
 *
 * kind 는 불투명 문자열이다(이 store 는 해석하지 않는다). 외부 스토어(subscribe/getSnapshot)로만
 * 노출한다. 그래서 창을 최소화한 뒤 화면을 옮겨도 스택이 유지되고 복원된다.
 */

import type { ReactNode } from "react";

// 동시에 열 수 있는 전체화면 창 수의 상한이다. 초과하면 open 이 null 을 반환한다(열기 차단).
export const MAX_WINDOWS = 5;

export type WindowEntry = {
  readonly id: string;
  // 창 종류다(호스트 렌더 분기 키). 의미는 소비처가 안다. store 는 불투명 문자열로 다룬다.
  readonly kind: string;
  // kind 안의 중복 방지 키다(예: datasetUuid). 같은 (kind,key)면 새로 열지 않고 기존 창을 활성화한다.
  readonly key: string;
  // dock 라벨(본문)이다. 소비처가 데이터를 로드한 뒤 update 로 고친다.
  readonly title: string;
  // dock 표시 슬롯이다. 없으면 title 을 표시한다. 소비처가 rich label 이 필요할 때만 채운다.
  readonly label: ReactNode | null;
  // dock eyebrow(보조 라벨)다. 없으면 null 이다.
  readonly subtitle: string | null;
  // 닫기 허용 여부다. false 면 close 를 무시한다(진행 중 업로드처럼 완료/폐기 전 닫기 금지).
  // 상태가 terminal 로 바뀌면 소비처가 update 로 true 로 바꾼다. 기본값은 true 다(대부분의 창은 언제든 닫힌다).
  readonly closable: boolean;
  // 콘텐츠가 읽는 kind별 payload 다. store 는 해석하지 않고, 소비처가 캐스팅한다.
  readonly params: unknown;
};

export type WindowStackSnapshot = {
  readonly entries: readonly WindowEntry[];
  // 전체화면으로 보이는 엔트리 id 다. null 이면 모두 최소화 상태다(배경+dock 표시).
  readonly activeId: string | null;
};

export type OpenWindowInput = {
  kind: string;
  key: string;
  title: string;
  label?: ReactNode;
  subtitle?: string;
  params: unknown;
  // 닫기 허용 여부다(기본 true). false 면 열린 뒤에도 close 를 무시한다. 진행 중 업로드 창처럼
  // 완료/폐기 전 닫기를 막을 때 쓴다. 상태가 terminal 로 바뀌면 소비처가 update({closable:true})로 바꾼다.
  closable?: boolean;
  // 전역 cap(MAX_WINDOWS)을 무시하고 항상 연다. 창이 반드시 있어야 하는 kind 전용이다(예: 진행 중
  // 업로드. 서버 잡은 반드시 대응 창을 가진다). 자체 상한은 소비처가 따로 적용한다.
  bypassCap?: boolean;
  // 같은 kind 의 다른 엔트리를 원자적으로 교체한다. 항상 1개만 유지하는 kind 에 쓴다(예: review).
  singleton?: boolean;
  // 엔트리가 닫힐 때(close/singleton 교체) 1회 호출한다. 정리 작업에 쓴다(예: 목록 새로고침).
  onClose?: () => void;
};

type WindowEntryPatch = Partial<
  Pick<WindowEntry, "title" | "label" | "subtitle" | "params" | "closable">
>;

// 빈 스냅샷이다. close 마다 새 객체를 만들지 않도록 인스턴스 하나를 다시 쓴다(getSnapshot 안정성).
const emptySnapshot: WindowStackSnapshot = { entries: [], activeId: null };

export class WindowStackManager {
  private readonly listeners = new Set<() => void>();
  private snapshot: WindowStackSnapshot = emptySnapshot;
  // 엔트리별 onClose 훅이다. snapshot 밖에 둔다(렌더 트리거·identity 와 무관).
  private readonly closeHandlers = new Map<string, () => void>();
  private seq = 0;
  private clearing = false;

  getSnapshot = (): WindowStackSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // (kind, key) 의 엔트리를 찾는다. 같은 kind 를 key 로 나눠 여는 창(제출 위저드의 session/schedule)이
  // 있는지 확인할 때 쓴다. open 의 복원 판정과 같은 기준이다.
  entryOf = (kind: string, key: string): WindowEntry | undefined =>
    this.snapshot.entries.find((entry) => entry.kind === kind && entry.key === key);

  /**
   * 새 전체화면 창을 활성 상태로 연다. 열린 엔트리 id 를 반환한다. cap 초과로 열지 못하면 null 을 반환한다.
   * - 같은 (kind,key)가 이미 있으면 새로 열지 않는다. 그 엔트리를 활성화(복원)하고 id 를 반환한다.
   * - singleton kind 면 같은 kind 의 기존 엔트리를 원자적으로 교체한다(직전 onClose 호출).
   * - bypassCap=true 면 cap 을 무시하고 항상 연다(창이 반드시 있어야 하는 kind 전용).
   * - 그 외에는 push 하고 activeId 를 새 엔트리로 둔다(직전 활성 창은 자동 최소화).
   */
  open = (input: OpenWindowInput): string | null => {
    if (this.clearing) {
      return null;
    }
    const entries = this.snapshot.entries;
    const existing = entries.find((entry) => entry.kind === input.kind && entry.key === input.key);
    if (existing !== undefined) {
      this.setActive(existing.id);
      return existing.id;
    }

    let base = entries;
    let replaced: readonly WindowEntry[] = [];
    if (input.singleton === true) {
      replaced = entries.filter((entry) => entry.kind === input.kind);
      if (replaced.length > 0) {
        base = entries.filter((entry) => entry.kind !== input.kind);
      }
    }

    if (input.bypassCap !== true && base.length >= MAX_WINDOWS) {
      return null;
    }

    // cap 검사를 통과한 뒤에만 교체 대상의 onClose 를 호출한다. 부수효과를 commit 과 함께 원자적으로 실행한다.
    for (const entry of replaced) {
      this.runCloseHandler(entry.id);
    }

    const id = `win-${(this.seq += 1)}`;
    const entry: WindowEntry = {
      id,
      kind: input.kind,
      key: input.key,
      title: input.title,
      label: input.label ?? null,
      subtitle: input.subtitle ?? null,
      closable: input.closable ?? true,
      params: input.params,
    };
    if (input.onClose !== undefined) {
      this.closeHandlers.set(id, input.onClose);
    }
    this.commit([...base, entry], id);
    return id;
  };

  // 해당 엔트리를 전체화면으로 표시한다(복원/전환). 직전 활성 창은 자동으로 최소화된다(activeId 는 하나).
  setActive = (id: string): void => {
    if (this.snapshot.activeId === id) {
      return;
    }
    if (!this.snapshot.entries.some((entry) => entry.id === id)) {
      return;
    }
    this.commit(this.snapshot.entries, id);
  };

  // 활성 창을 최소화한다(activeId=null → 배경+dock 표시). id 를 지정하면 그 엔트리가 활성일 때만 최소화한다.
  minimize = (id?: string): void => {
    if (this.snapshot.activeId === null) {
      return;
    }
    if (id !== undefined && this.snapshot.activeId !== id) {
      return;
    }
    this.commit(this.snapshot.entries, null);
  };

  // 엔트리를 닫고(제거) onClose 를 호출한다. 활성이었으면 activeId 를 비운다.
  // closable=false 엔트리는 닫지 않는다(예: 진행 중 업로드). 먼저 update 로 closable 을 바꿔야 한다.
  close = (id: string): void => {
    const target = this.snapshot.entries.find((entry) => entry.id === id);
    if (target !== undefined && !target.closable) {
      return;
    }
    const entries = this.snapshot.entries.filter((entry) => entry.id !== id);
    if (entries.length === this.snapshot.entries.length) {
      return;
    }
    const activeId = this.snapshot.activeId === id ? null : this.snapshot.activeId;
    this.commit(entries, activeId);
    this.runCloseHandler(id);
  };

  /**
   * 세션에 속한 모든 창을 강제로 닫는다.
   *
   * 진행 중 업로드처럼 `closable=false`인 창도 세션 경계에서는 남길 수 없으므로 제거한다. 각
   * `onClose`는 정확히 한 번 실행하고, 하나가 실패해도 나머지를 끝까지 실행한 뒤 첫 오류를 전달한다.
   */
  clear = (): void => {
    if (this.snapshot.entries.length === 0) {
      return;
    }
    const entries = [...this.snapshot.entries];
    this.clearing = true;
    let firstError: unknown = null;
    try {
      try {
        this.commit([], null);
      } catch (error) {
        firstError = error;
      }
      for (const entry of entries) {
        try {
          this.runCloseHandler(entry.id);
        } catch (error) {
          firstError ??= error;
        }
      }
    } finally {
      this.clearing = false;
    }
    if (firstError !== null) {
      throw firstError;
    }
  };

  // dock 라벨/콘텐츠 params 를 고친다(예: 데이터 로드 후 제목, 리뷰 스텝 변경). 바뀐 값이 없으면 무시한다.
  update = (id: string, patch: WindowEntryPatch): void => {
    let changed = false;
    const entries = this.snapshot.entries.map((entry) => {
      if (entry.id !== id) {
        return entry;
      }
      const next = { ...entry, ...patch };
      if (
        next.title === entry.title &&
        next.label === entry.label &&
        next.subtitle === entry.subtitle &&
        next.closable === entry.closable &&
        next.params === entry.params
      ) {
        return entry;
      }
      changed = true;
      return next;
    });
    if (!changed) {
      return;
    }
    this.commit(entries, this.snapshot.activeId);
  };

  private runCloseHandler(id: string): void {
    const handler = this.closeHandlers.get(id);
    this.closeHandlers.delete(id);
    handler?.();
  }

  private commit(entries: readonly WindowEntry[], activeId: string | null): void {
    this.snapshot = entries.length === 0 ? emptySnapshot : { entries, activeId };
    for (const listener of this.listeners) {
      listener();
    }
  }
}
